/**
 * Admin AI Models - NexusApi Zod Schemas
 *
 * API contract schemas for the admin AI models management endpoints.
 * These models represent the global catalog of AI models available on the platform.
 *
 * Base entity schemas live in shared/domain/admin-ai-models/ai-model.schemas.ts.
 * This file wraps their responses in ApiSuccessSchema for NexusApi compliance.
 */

import { z } from "zod";

import { DbEnum } from "../../../generated/prisma-enums";
import { LongContextPricingSchema } from "../../../models/model-catalog.config";
import { ThinkingDialectSchema } from "../../../models/thinking-dialect.registry";
import {
  AiModelSchema,
  DeadModelResourcesSchema
} from "../../../shared/domain/admin-ai-models/ai-model.schemas";
import { DroppedRepointLevelSchema } from "../../../shared/domain/admin-ai-models/repoint.schemas";
import { ApiSuccessSchema } from "../../common/response.schema";

// ============================================
// Re-export base enums for controller use
// ============================================

export const AdminAiModelProviderEnum = z.enum(DbEnum.MODEL_PROVIDER_VALUES);
export const AdminAiModelResolutionEnum = z.enum(["PENDING", "APPROVED", "IGNORED"]);
const AdminAiModelSystemRoleEnum = z.enum(["system", "developer", "user"]);
const AdminAiModelApiTypeEnum = z.enum(["completions", "responses"]);

// ============================================
// ZAdminAiModels — NexusApi Endpoint Definitions
// ============================================

export const ZAdminAiModels = {
  /**
   * List all AI models with optional filtering
   * GET /api/admin/ai-models
   */
  List: {
    method: "GET" as const,
    path: "/api/admin/ai-models",
    Query: z.object({
      provider: AdminAiModelProviderEnum.optional(),
      enabled: z
        .string()
        .optional()
        .transform((v) => (v === "true" ? true : v === "false" ? false : undefined)),
      isCustom: z
        .string()
        .optional()
        .transform((v) => (v === "true" ? true : v === "false" ? false : undefined)),
      resolution: AdminAiModelResolutionEnum.optional(),
      search: z.string().optional(),
      deprecated: z
        .string()
        .optional()
        .transform((v) => (v === "true" ? true : v === "false" ? false : undefined))
    }),
    Response: ApiSuccessSchema(
      z.object({
        models: z.array(AiModelSchema),
        total: z.number()
      })
    )
  },

  /**
   * Get count of pending (unresolved) AI models
   * GET /api/admin/ai-models/pending-count
   */
  PendingCount: {
    method: "GET" as const,
    path: "/api/admin/ai-models/pending-count",
    Response: ApiSuccessSchema(z.object({ count: z.number() }))
  },

  /**
   * Bulk approve all pending AI models
   * POST /api/admin/ai-models/bulk-approve
   */
  BulkApprove: {
    method: "POST" as const,
    path: "/api/admin/ai-models/bulk-approve",
    Response: ApiSuccessSchema(z.object({ approved: z.number() }))
  },

  /**
   * Sync AI models from code defaults (models.json)
   * POST /api/admin/ai-models/sync
   */
  Sync: {
    method: "POST" as const,
    path: "/api/admin/ai-models/sync",
    Response: ApiSuccessSchema(z.object({ added: z.number() }))
  },

  /**
   * Reset all non-custom models to factory defaults
   * POST /api/admin/ai-models/reset-all
   */
  ResetAll: {
    method: "POST" as const,
    path: "/api/admin/ai-models/reset-all",
    Response: ApiSuccessSchema(
      z.object({
        deleted: z.number(),
        reset: z.number()
      })
    )
  },

  /**
   * Get model health — dead models and their resource usage counts
   * GET /api/admin/ai-models/model-health
   */
  ModelHealth: {
    method: "GET" as const,
    path: "/api/admin/ai-models/model-health",
    Response: ApiSuccessSchema(
      z.object({
        deadModels: z.array(DeadModelResourcesSchema),
        totalAffected: z.number()
      })
    )
  },

  /**
   * Batch-migrate resources from dead models to replacement models
   * POST /api/admin/ai-models/migrate-resources
   */
  MigrateResources: {
    method: "POST" as const,
    path: "/api/admin/ai-models/migrate-resources",
    Body: z.object({
      migrations: z
        .array(
          z.object({
            fromModelName: z.string(),
            toModelName: z.string()
          })
        )
        .min(1)
    }),
    Response: ApiSuccessSchema(
      z.object({
        updated: z.object({
          agents: z.number(),
          tasks: z.number(),
          /** `AgentTask` rows repointed — the legacy table, not workflow graph nodes. */
          workflowNodes: z.number(),
          /**
           * Workflow graph nodes whose `data.modelOverride` was repointed, counted per
           * node array: a node overridden in both the draft and the published graph
           * counts twice.
           */
          workflowNodeOverrides: z.number()
        }),
        levelsDropped: z.array(DroppedRepointLevelSchema)
      })
    )
  },

  /**
   * Get a single AI model by modelId
   * GET /api/admin/ai-models/:modelId
   */
  Get: {
    method: "GET" as const,
    path: "/api/admin/ai-models/:modelId",
    PathVars: z.object({ modelId: z.string() }),
    Response: ApiSuccessSchema(AiModelSchema)
  },

  /**
   * Create a custom AI model
   * POST /api/admin/ai-models
   */
  Create: {
    method: "POST" as const,
    path: "/api/admin/ai-models",
    Body: z.object({
      modelId: z.string().min(1).max(100),
      provider: AdminAiModelProviderEnum,
      displayName: z.string().trim().min(1).max(255),
      modelDisplayName: z.string().min(1).max(100).optional(),
      modelName: z.string().min(1).max(100),
      enabled: z.boolean().optional(),
      inputTokenPrice: z.number().min(0),
      outputTokenPrice: z.number().min(0),
      cacheReadTokenPrice: z.number().min(0).optional(),
      cacheWriteTokenPrice: z.number().min(0).optional(),
      longContextPricing: LongContextPricingSchema.nullable().optional(),
      contextSize: z.number().int().positive().optional(),
      streaming: z.boolean().optional(),
      systemRole: AdminAiModelSystemRoleEnum.optional(),
      codeInterpreter: z.boolean().optional(),
      underlyingModel: z.string().optional(),
      claudeCodeModelId: z.string().optional().nullable(),
      fastMode: z.boolean().optional(),
      thinkingDialect: ThinkingDialectSchema.optional(),
      maxOutputTokens: z.number().int().positive(),
      apiType: AdminAiModelApiTypeEnum.optional(),
      notes: z.string().optional()
    }),
    Response: ApiSuccessSchema(AiModelSchema)
  },

  /**
   * Update an AI model (admin-configurable fields only)
   * PATCH /api/admin/ai-models/:modelId
   */
  Update: {
    method: "PATCH" as const,
    path: "/api/admin/ai-models/:modelId",
    PathVars: z.object({ modelId: z.string() }),
    // Only admin-configurable fields. Factory/routing fields (streaming, contextSize,
    // systemRole, apiType, codeInterpreter, underlyingModel, claudeCodeModelId, fastMode,
    // thinkingDialect) are catalog-owned: a changed value reaches stored rows through the
    // data migration packages/types/src/models/README.md § Stored rows describes, and they
    // are not editable through the admin UI.
    Body: z.object({
      displayName: z.string().trim().min(1).max(255).optional(),
      modelDisplayName: z.string().min(1).max(100).optional(),
      inputTokenPrice: z.number().min(0).optional(),
      outputTokenPrice: z.number().min(0).optional(),
      cacheReadTokenPrice: z.number().min(0).nullable().optional(),
      cacheWriteTokenPrice: z.number().min(0).nullable().optional(),
      longContextPricing: LongContextPricingSchema.nullable().optional(),
      enabled: z.boolean().optional(),
      deprecated: z.boolean().optional(),
      notes: z.string().nullable().optional()
    }),
    Response: ApiSuccessSchema(AiModelSchema)
  },

  /**
   * Delete a custom AI model (only custom models can be deleted)
   * DELETE /api/admin/ai-models/:modelId
   */
  Delete: {
    method: "DELETE" as const,
    path: "/api/admin/ai-models/:modelId",
    PathVars: z.object({ modelId: z.string() }),
    Response: ApiSuccessSchema(z.object({}))
  },

  /**
   * Toggle AI model enabled status
   * POST /api/admin/ai-models/:modelId/toggle
   */
  Toggle: {
    method: "POST" as const,
    path: "/api/admin/ai-models/:modelId/toggle",
    PathVars: z.object({ modelId: z.string() }),
    Response: ApiSuccessSchema(AiModelSchema)
  },

  /**
   * Resolve a pending AI model (approve or ignore)
   * POST /api/admin/ai-models/:modelId/resolve
   */
  Resolve: {
    method: "POST" as const,
    path: "/api/admin/ai-models/:modelId/resolve",
    PathVars: z.object({ modelId: z.string() }),
    Body: z.object({
      action: z.enum(["APPROVE", "IGNORE"]),
      enabled: z.boolean().optional()
    }),
    Response: ApiSuccessSchema(AiModelSchema)
  },

  /**
   * Reset a single model to factory defaults
   * Preserves: enabled, resolution, notes
   * POST /api/admin/ai-models/:modelId/reset
   */
  Reset: {
    method: "POST" as const,
    path: "/api/admin/ai-models/:modelId/reset",
    PathVars: z.object({ modelId: z.string() }),
    Response: ApiSuccessSchema(AiModelSchema)
  }
} as const;
